import React, { useEffect, useMemo, useState } from "react";
import api, { describeAxiosError } from "@/lib/api";
import GoogleConnectCard from "@/components/GoogleConnectCard";
import { toast } from "@/components/ui/sonner";
import { X, CalendarPlus, VideoCamera, MapPin, Phone, Sparkle } from "@phosphor-icons/react";

const DURATIONS = [15, 30, 45, 60, 90, 120];

const INTERVIEW_TYPES = [
    {
        id: "virtual",
        label: "Virtual",
        hint: "Google Meet link auto-generated",
        Icon: VideoCamera,
    },
    {
        id: "face_to_face",
        label: "Face to face",
        hint: "On-site / office location",
        Icon: MapPin,
    },
    {
        id: "telephonic",
        label: "Telephonic",
        hint: "Phone call at scheduled time",
        Icon: Phone,
    },
];

// Default to "now + 1 hour, rounded to next 15-min slot" in the browser's TZ.
function defaultStart() {
    const d = new Date(Date.now() + 60 * 60 * 1000);
    d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function browserTimezone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";
    } catch {
        return "Asia/Kolkata";
    }
}

export default function ScheduleInterviewModal({ candidate, job, onClose, onScheduled }) {
    const [gstatus, setGstatus] = useState(null);
    // V1.1 #12 — workspace-level toggle. When false (default) we use the
    // native ICS + Jitsi path and DO NOT require Google to be connected.
    const [gcalEnabled, setGcalEnabled] = useState(false);
    const [interviewType, setInterviewType] = useState("virtual");
    const [locationText, setLocationText] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [start, setStart] = useState(defaultStart());
    const [duration, setDuration] = useState(30);
    const [tz, setTz] = useState(browserTimezone());
    const [summary, setSummary] = useState("");
    const [description, setDescription] = useState("");
    const [extra, setExtra] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState("");
    // Auto-suggest slots (from recruiter's Google Calendar freeBusy).
    const [suggesting, setSuggesting] = useState(false);
    const [suggestions, setSuggestions] = useState([]); // [{start_iso,end_iso}]
    const [suggestErr, setSuggestErr] = useState("");

    useEffect(() => {
        api.get("/auth/google/status").then((r) => setGstatus(r.data)).catch(() => setGstatus({ connected: false }));
        api.get("/settings/calendar")
            .then((r) => setGcalEnabled(!!r.data.google_calendar_sync_enabled))
            .catch(() => setGcalEnabled(false));
    }, []);

    const minLocal = useMemo(() => {
        const now = new Date();
        const pad = (n) => String(n).padStart(2, "0");
        return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
    }, []);

    // Convert ISO (UTC) → local "YYYY-MM-DDTHH:mm" for the datetime-local input.
    const isoToLocalInput = (iso) => {
        const d = new Date(iso);
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    const fetchSuggestions = async () => {
        setSuggestErr("");
        setSuggesting(true);
        try {
            const { data } = await api.get(
                `/interviews/suggest-slots?duration_min=${Number(duration)}&days_ahead=7&max_slots=6`,
            );
            setSuggestions(data.slots || []);
            if ((data.slots || []).length === 0) {
                toast.info("No free slots in the next 7 days — try a shorter duration.");
            }
        } catch (ex) {
            const msg = describeAxiosError(ex);
            setSuggestErr(msg);
            toast.error(msg);
        } finally {
            setSuggesting(false);
        }
    };

    const pickSuggestion = (slot) => {
        setStart(isoToLocalInput(slot.start_iso));
        toast.success("Slot picked — review and click Schedule to confirm.");
    };

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setSubmitting(true);
        try {
            // Convert local "YYYY-MM-DDTHH:mm" to ISO with the browser's offset.
            // The user-selected tz is informational for Google; the actual moment
            // is the local datetime the user picked.
            const localDt = new Date(start);
            const isoWithOffset = localDt.toISOString();
            const payload = {
                start: isoWithOffset,
                duration_min: Number(duration),
                timezone: tz,
                interview_type: interviewType,
                location: interviewType === "face_to_face" ? locationText.trim() : "",
                phone_number: interviewType === "telephonic" ? phoneNumber.trim() : "",
                summary: summary.trim() || undefined,
                description: description.trim() || undefined,
                extra_attendees: extra
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean),
            };
            const { data } = await api.post(
                `/candidates/${candidate.id}/schedule-interview`,
                payload,
            );
            const desc =
                data.interview_type === "virtual"
                    ? `Meet link generated. Invite sent to ${candidate.email}.`
                    : data.interview_type === "face_to_face"
                      ? `Invite sent. Location: ${data.location || locationText}.`
                      : `Invite sent. Candidate will receive a call at ${data.phone_number || phoneNumber}.`;
            toast.success("Invite sent successfully", {
                description: desc,
                action:
                    data.meet_link
                        ? {
                              label: "Open Meet",
                              onClick: () => window.open(data.meet_link, "_blank", "noopener,noreferrer"),
                          }
                        : undefined,
            });
            onScheduled?.(data);
            onClose?.();
        } catch (ex) {
            setErr(describeAxiosError(ex));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="schedule-interview-modal"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-white border border-[#E5E5E5] w-full max-w-xl max-h-[90vh] overflow-y-auto"
            >
                <div className="flex items-center justify-between p-5 border-b border-[#E5E5E5] sticky top-0 bg-white">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">
                            Interview
                        </p>
                        <h2 className="font-heading text-2xl font-bold tracking-tight">
                            Schedule with {candidate.name}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="close-schedule-modal"
                        className="p-2 hover:bg-[#F5F5F5]"
                    >
                        <X size={18} weight="bold" />
                    </button>
                </div>

                {/* Form (modal closes + toast appears on success) */}
                <form onSubmit={submit} className="p-5 space-y-4">
                        {gcalEnabled && gstatus && !gstatus.connected && (
                            <div className="border border-[#EFF6FF] bg-[#EFF6FF] p-4 space-y-2">
                                <p className="text-sm font-bold text-[#1E3A8A]">
                                    Connect Google Calendar (optional)
                                </p>
                                <p className="text-xs text-[#1E3A8A]">
                                    Your admin has enabled Google Calendar sync. Connect your account
                                    to have this event also appear in your personal Google Calendar
                                    with a Google Meet link. Otherwise, we&apos;ll send an .ics invite
                                    with a Jitsi Meet link — no connection required.
                                </p>
                                <GoogleConnectCard compact />
                            </div>
                        )}

                        {/* Attendees preview */}
                        <div className="border border-[#E5E5E5] bg-[#FAFAFA] p-3" data-testid="schedule-attendees-preview">
                            <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] mb-2">
                                Auto-invited attendees
                            </p>
                            <ul className="space-y-1 text-xs text-[#525252]">
                                <li className="flex items-center justify-between">
                                    <span><span className="font-bold text-ink">{candidate.name}</span> · candidate</span>
                                    <span className="text-[#A3A3A3]">{candidate.email}</span>
                                </li>
                                {job?.hiring_manager ? (
                                    <li className="flex items-center justify-between" data-testid="hm-attendee-row">
                                        <span><span className="font-bold text-ink">{job.hiring_manager.name}</span> · hiring manager</span>
                                        <span className="text-[#A3A3A3]">{job.hiring_manager.calendar_email || job.hiring_manager.email}</span>
                                    </li>
                                ) : job?.hiring_manager_id ? null : (
                                    <li className="italic text-[#A3A3A3]">No hiring manager tagged on this requirement.</li>
                                )}
                                <li className="italic text-[#A3A3A3]">+ all admins (auto)</li>
                            </ul>
                        </div>

                        {/* Interview type */}
                        <div>
                            <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-2 block">
                                Interview type
                            </label>
                            <div className="grid grid-cols-3 gap-2" data-testid="interview-type-group">
                                {INTERVIEW_TYPES.map(({ id, label, hint, Icon }) => {
                                    const active = interviewType === id;
                                    return (
                                        <button
                                            type="button"
                                            key={id}
                                            data-testid={`interview-type-${id}`}
                                            onClick={() => setInterviewType(id)}
                                            className={`text-left p-3 border transition-colors ${
                                                active
                                                    ? "border-cobalt bg-cobalt/5"
                                                    : "border-[#E5E5E5] hover:border-ink"
                                            }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <Icon size={14} weight="bold" className={active ? "text-cobalt" : "text-[#525252]"} />
                                                <span className={`text-xs font-bold uppercase tracking-wider ${active ? "text-cobalt" : "text-ink"}`}>
                                                    {label}
                                                </span>
                                            </div>
                                            <p className="text-[10px] text-[#A3A3A3] mt-1 leading-snug">
                                                {hint}
                                            </p>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Conditional fields per type */}
                        {interviewType === "face_to_face" && (
                            <div data-testid="face-to-face-fields">
                                <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1 block">
                                    Location / address
                                </label>
                                <textarea
                                    data-testid="schedule-location-input"
                                    rows={2}
                                    value={locationText}
                                    onChange={(e) => setLocationText(e.target.value)}
                                    required
                                    placeholder="e.g. 4th floor, Tower B, DLF Cybercity, Gurugram"
                                    className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-cobalt"
                                />
                                {job?.hiring_manager?.name && (
                                    <p className="mt-1 text-[10px] text-[#A3A3A3]">
                                        Candidate will meet <span className="font-bold text-ink">{job.hiring_manager.name}</span>.
                                    </p>
                                )}
                            </div>
                        )}

                        {interviewType === "telephonic" && (
                            <div data-testid="telephonic-fields">
                                <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1 block">
                                    Call number {job?.hiring_manager?.name ? `(${job.hiring_manager.name} will call the candidate)` : ""}
                                </label>
                                <input
                                    type="tel"
                                    data-testid="schedule-phone-input"
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                    required
                                    placeholder="+91 9876543210"
                                    pattern="^\+?[0-9\s\-().]{10,25}$"
                                    title="Enter 10–15 digits; '+' and spaces/dashes are allowed"
                                    className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-cobalt"
                                />
                                <p className="mt-1 text-[10px] text-[#A3A3A3]">
                                    Shown in the calendar invite so candidate knows what number to expect / call.
                                </p>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2 sm:col-span-1">
                                <div className="flex items-center justify-between mb-1">
                                    <label className="text-[10px] font-black uppercase tracking-wider text-[#525252]">
                                        Date &amp; time
                                    </label>
                                    <button
                                        type="button"
                                        data-testid="schedule-suggest-slots-btn"
                                        onClick={fetchSuggestions}
                                        disabled={suggesting || (gstatus && !gstatus.connected)}
                                        className="text-[10px] font-black uppercase tracking-wider text-cobalt hover:text-cobalt-dark disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
                                    >
                                        <Sparkle size={10} weight="bold" />
                                        {suggesting ? "Finding…" : "Suggest slots"}
                                    </button>
                                </div>
                                <input
                                    type="datetime-local"
                                    data-testid="schedule-start-input"
                                    value={start}
                                    min={minLocal}
                                    required
                                    onChange={(e) => setStart(e.target.value)}
                                    className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-cobalt"
                                />
                            </div>
                            <div className="col-span-2 sm:col-span-1">
                                <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1 block">
                                    Duration
                                </label>
                                <select
                                    data-testid="schedule-duration-select"
                                    value={duration}
                                    onChange={(e) => setDuration(e.target.value)}
                                    className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-cobalt"
                                >
                                    {DURATIONS.map((d) => (
                                        <option key={d} value={d}>
                                            {d} min
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Suggested slots (Google Calendar freeBusy) */}
                        {suggestErr && !suggesting && (
                            <div
                                data-testid="schedule-suggest-error"
                                className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-xs px-3 py-2"
                            >
                                {suggestErr}
                            </div>
                        )}
                        {suggestions.length > 0 && (
                            <div
                                data-testid="schedule-suggest-slots-list"
                                className="border border-cobalt/25 bg-cobalt/5 px-3 py-3"
                            >
                                <p className="text-[10px] font-black uppercase tracking-wider text-cobalt mb-2 inline-flex items-center gap-1">
                                    <Sparkle size={10} weight="bold" /> Free slots · IST business hours
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                    {suggestions.map((s, idx) => {
                                        const d = new Date(s.start_iso);
                                        const label = d.toLocaleString(undefined, {
                                            weekday: "short",
                                            day: "numeric",
                                            month: "short",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        });
                                        return (
                                            <button
                                                key={idx}
                                                type="button"
                                                data-testid={`schedule-suggest-slot-${idx}`}
                                                onClick={() => pickSuggestion(s)}
                                                className="text-[11px] font-bold bg-white border border-cobalt/40 hover:border-cobalt hover:bg-cobalt hover:text-white px-2.5 py-1.5 transition-colors"
                                            >
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div>
                            <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1 block">
                                Time zone
                            </label>
                            <input
                                type="text"
                                value={tz}
                                onChange={(e) => setTz(e.target.value)}
                                placeholder="Asia/Kolkata"
                                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-cobalt"
                            />
                        </div>

                        <div>
                            <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1 block">
                                Title (optional)
                            </label>
                            <input
                                type="text"
                                value={summary}
                                onChange={(e) => setSummary(e.target.value)}
                                placeholder={`Interview: ${candidate.name}`}
                                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-cobalt"
                            />
                        </div>

                        <div>
                            <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1 block">
                                Description (optional)
                            </label>
                            <textarea
                                rows={3}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Add a note for the candidate…"
                                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-cobalt"
                            />
                        </div>

                        <div>
                            <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1 block">
                                Extra attendees (comma-separated, optional)
                            </label>
                            <input
                                type="text"
                                value={extra}
                                onChange={(e) => setExtra(e.target.value)}
                                placeholder="hiring.manager@example.com, panelist@example.com"
                                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-cobalt"
                            />
                            <p className="mt-1 text-[10px] text-[#A3A3A3]">
                                The candidate ({candidate.email}) and all admins are added automatically.
                            </p>
                        </div>

                        {err && (
                            <div
                                data-testid="schedule-error"
                                className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-sm px-3 py-2"
                            >
                                {err}
                            </div>
                        )}

                        <div className="flex gap-3 pt-1">
                            <button
                                type="button"
                                onClick={onClose}
                                className="border border-[#E5E5E5] hover:border-ink px-5 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                data-testid="schedule-submit-button"
                                disabled={submitting || (gcalEnabled && gstatus && !gstatus.connected)}
                                className="btn-gradient flex-1 px-5 py-2.5 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2"
                            >
                                <CalendarPlus size={14} weight="bold" />
                                {submitting ? "Creating event…" : "Schedule & send invite"}
                            </button>
                        </div>
                    </form>
            </div>
        </div>
    );
}
